<template>
    <div class="py-28 pb-20 text-white hero">
        <div
            class="flex flex-col lg:flex-row justify-between items-center gap-8 max-w-7xl mx-auto px-4 pb-20 lg:text-left text-center">
            <div class="text-pretty">
                <h1 class="font-bold lg:text-4xl text-3xl font-normal">Find the Right Slide, Instantly
                </h1>
                <div class="mt-4 text-lg">
                    <p>Boost productivity with accurate slide search on Sharepoint or Google Drive.
                    </p>
                </div>
                <div class="flex lg:flex-row flex-col gap-4 items-start lg:items-center items-center mt-8 lg:mt-8">
                    <Calendly name="requestdemo" href="https://calendly.com/d/dj2-4jd-3tw/introductory-call-pathway"
                        class="flex gap-2 items-center font-bold text-xl hover:bg-transparent glow-btn block max-w-max bg-[#dc280b] px-6 py-3 rounded-lg border-2 border-[#dc280b] !transition">
                        <Icon name="heroicons:check-circle-20-solid" /> Request a Demo
                    </Calendly>
                    <a href="https://search-your-slide-decks.demo.pathway.com/#search-your-slide-decks "
                        class="flex gap-2 items-center font-bold text-xl bg-transparent block max-w-max hover:bg-[#dc280b] px-6 py-3 rounded-lg border-2 border-[#dc280b] !transition">
                        Try it out
                    </a>
                </div>

            </div>
            <div>
                <div>
                    <video width="640" height="480" controls
                        class="relative w-full max-w-[30rem] md:min-w-[30rem] min-w-full shadow-[5px_5px_0px_0px_rgba(24,24,64)]">
                        <source src="https://d14l3brkh44201.cloudfront.net/assets/solutions/rag-slides-search-video.mp4"
                            type="video/mp4">
                        Your browser does not support the video tag.
                    </video>
                </div>
            </div>
        </div>
    </div>
    <div>
        <TrustedBy />
    </div>
    <hr />
    <div class=" bg-white">
        <div class="max-w-7xl mx-auto md:p-14 px-4 py-14 pt-28 text-[#232a34]" id="ask-questions">
            <h2 class="text-4xl font-semibold text-center">How it helps</h2>
            <div class="mt-16 flex flex-col gap-16">
                <div class="flex flex-col md:flex-row gap-8 justify-between items-center">
                    <div class="md:w-1/2">
                        <span class="text-2xl pw-text-black font-normal">Improved Efficiency</span>
                        <p class="mt-2 text-balance"><span class="font-semibold">Save Efforts</span>: No more manual
                            sifting through countless presentations.
                        </p>
                        <p class="mt-2 text-balance"><span class="font-semibold">Faster Information Retrieval</span>:
                            Instantly find specific information with a few keywords or descriptive prompts, saving time
                            when preparing for presentations or reviewing past projects.
                        </p>
                    </div>
                    <div class="w-full md:w-auto">
                        <div
                            class="md:w-[30rem] w-full h-64 bg-[#F8FAFC] rounded-lg flex items-center justify-center font-bold text-gray-200">
                            <ZoomImage src="/assets/solutions/rag-slides-screenshot-1.png" alt=""
                                class="rounded-lg object-contain w-full h-full" provider="s3" />
                        </div>
                    </div>
                </div>
                <div class="flex flex-col md:flex-row gap-8 justify-between items-center">
                    <div class="md:w-1/2">
                        <span class="text-2xl pw-text-black font-normal">Enhanced Organization </span>
                        <p class="mt-2 text-balance"><span class="font-semibold">Automated Categorization</span>:
                            Organize your slide library by topic, project, or other criteria.
                        </p>
                        <p class="mt-2 text-balance"><span class="font-semibold">Improved Knowledge Management</span>:
                            Build on existing knowledge to foster institutional memory and knowledge sharing.
                        </p>
                    </div>
                    <div class="w-full md:w-auto">
                        <div
                            class="md:w-[30rem] w-full h-64 bg-[#F8FAFC] rounded-lg flex items-center justify-center font-bold text-gray-200">
                            <ZoomImage src="/assets/solutions/rag-slides-screenshot-2.png" alt=""
                                class="rounded-lg object-contain w-full h-full" provider="s3" />
                        </div>
                    </div>
                </div>
                <div class="flex flex-col md:flex-row gap-8 justify-between items-center">
                    <div class="md:w-1/2">
                        <span class="text-2xl pw-text-black font-normal">Enhanced Reliability </span>
                        <p class="mt-2 text-balance"><span class="font-semibold">Automatic Updates</span>:
                            Hybrid indexes update automatically whenever a new slide is added or removed, ensuring your
                            information is always current and accurate.
                        </p>
                        <p class="mt-2 text-balance"><span class="font-semibold">Improved Accuracy</span>:
                            Analyze large volumes of slides with 98%+ accuracy, minimizing the risk of missing relevant
                            information.
                        </p>
                    </div>
                    <div class="w-full md:w-auto">
                        <div
                            class="md:w-[30rem] w-full h-64 bg-[#F8FAFC] rounded-lg flex items-center justify-center font-bold text-gray-200">
                            <ZoomImage src="/assets/solutions/rag-slides-screenshot-3.png"
                                alt="Always up-to-date and accurate retrieval with hybrid indexing screenshot"
                                class="rounded-lg object-contain w-full h-full" provider="s3" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <hr />
    <div class=" bg-[#F8FAFC]">
        <div class="max-w-7xl mx-auto md:p-14 px-4 py-14 text-[#232a34]">
            <h2 class="text-3xl font-bold" id="try-it-out">Try it out</h2>
            <h3 class="text-2xl font-medium">Interact with demo</h3>
            <div class="flex justify-between items-center flex-col md:flex-row  mt-4 my-8">
                <div class="flex gap-4 ">
                    <a href="https://drive.google.com/drive/folders/11ll7x0p8n68T0SWsk0cMbRqfVAL7xTMy" target="_blank"
                        class="flex items-center gap-4 p-4 bg-white rounded-lg border-2 border-white hover:border-primary-300 hover:underline transition">
                        <Icon name="logos:google-drive" class="w-10 h-10" />
                        <div class="flex flex-col gap-2">
                            <span class="text-primary-500">
                                Browse files
                            </span>
                        </div>
                    </a>
                </div>
                <div>

                    <a href="https://search-your-slide-decks.demo.pathway.com/#search-your-slide-decks" target="_blank"
                        class="bg-white block p-4  flex gap-4 items-center mt-4 border-2 hover:border-primary-800 hover:underline transition rounded-se-lg rounded-es-lg border-primary-500">
                        <div>
                            <img src="/assets/design/logo/logo-p.svg" alt="" class="w-10 h-10" />
                        </div>
                        <div>
                            <span class="font-semibold text-[#232a34]">Try out the demo</span>
                        </div>
                    </a>
                </div>
            </div>
            <div class="flex flex-col text-sm mt-4"><span><span class="font-semibold">DEMO: Uploaded files will be
                        visible to the public.</span></span><span>Data processing is subject to our <a
                        href="/privacy_gdpr_di#privacy-policy" class="text-primary-500 underline">privacy
                        policy</a></span></div>
        </div>
    </div>
    <hr id="reference-architecture" />
    <div class="bg-white">
        <div class="max-w-7xl mx-auto md:p-14 md:pb-24 px-4 py-14 text-[#222]">
            <h2 class="text-3xl font-bold text-center">Reference Architecture</h2>
            <div class="mt-8 ">
                <NuxtImg src="/assets/solutions/ai-slides-diagram.svg" alt="RAG Slides Search Use Case diagram"
                    class="mx-auto" provider="s3" />
            </div>
        </div>
    </div>
    <hr />
    <div class="w-full  bg-[#F8FAFC]">
        <div class="p-4 max-w-7xl mx-auto px-4 py-28">
            <div class="p-8 border-gray-300 border rounded-xl quote-slider">
                <div class="lg:p-8 p-2">
                    <p class="text-center md:text-xl text-lg font-light">Here comes an update on the input data source,
                        and
                        we get alerted that the LLM has changed its answer to my question. Just set a web hook, create a
                        reactive application in Pathway, and never need to care about synchronizing data again ❤️</p>
                    <span class="text-center block mt-8 text-xl">Pau Labarto Bajo</span>
                    <span class="text-center block mt-2 text-sm">Creator of Real World ML</span>
                </div>
            </div>
        </div>
    </div>
    <hr />
    <div class="bg-[#19242F]">
        <div class="max-w-7xl mx-auto md:p-8 md:py-16 md:pt-28 px-4 py-8 text-white">
            <span class="text-3xl font-semibold">Backed by Enterprise Security & Authentication</span>
            <div class="flex flex-col md:flex-row gap-8 justify-between items-center">
                <div class="md:w-[75%]">
                    <ul class="flex flex-col gap-4 mt-8">
                        <li class="flex gap-4 text-white font-light items-center text-xl"><img
                                src="/assets/landing/icon-check-gray.svg" alt="" class="w-8 h-8" /> <span>Host on your
                                cloud
                                or
                                on-premise</span>
                        </li>
                        <li class="flex gap-4 text-white font-light items-center text-xl"><img
                                src="/assets/landing/icon-check-gray.svg" alt="" class="w-8 h-8" /> <span>Secure by
                                Design</span>
                        </li>
                        <li class="flex gap-4 text-white font-light items-center text-xl"><img
                                src="/assets/landing/icon-check-gray.svg" alt="" class="w-8 h-8" /> <span>Granular
                                Access
                                Management</span>
                        </li>
                        <li class="flex gap-4 text-white font-light items-center text-xl"><img
                                src="/assets/landing/icon-check-gray.svg" alt="" class="w-8 h-8" />
                            <span>Compliance-Ready</span>
                        </li>
                    </ul>
                </div>
                <div class="flex items-center justify-center h-full">
                    <Calendly name="GetStarted" href="https://calendly.com/d/dj2-4jd-3tw/introductory-call-pathway"
                        class="flex gap-2 items-center text-white font-bold whitespace-nowrap text-xl hover:!bg-transparent block w-full md:max-w-max !bg-primary-500 px-6 py-3 rounded-lg border-2 border-primary-500 !transition">
                        Let's talk
                        <Icon name="heroicons:chat-bubble-left-right-20-solid" />
                    </Calendly>
                </div>
            </div>

        </div>
    </div>

</template>

<style scoped>
.hero {
    background: #141d2d;
    background: linear-gradient(90deg, #141d2d 42%, #152033 50%, #2729b0);
}

.glow-btn {
    box-shadow: 0 5px 15px rgba(243, 23, 23, 0.4);
}

td {
    padding: 0.5rem;
    border: 1px solid #F7F6FE;
    transition: background-color 0.4s;
}

tr:hover {
    background-color: #F8F6FF;
    transition: background-color 0.4s;
}

tr:last-of-type:hover {
    background-color: white;
}

table {
    border: 1px solid #F7F6FE;
    padding: 1rem;
}

.quote-slider {
    position: relative;

    &::before {
        content: "";
        position: absolute;
        top: -1rem;
        left: 50%;
        transform: translateX(-50%);
        width: 2rem;
        height: 2rem;
        padding: 1rem;
        background-color: #fff;
        background-image: url("/assets/landing/quote.svg");
        color: red;
        background-size: 50%;
        background-repeat: no-repeat;
        background-position: center;
        border: 1px solid #7B8088;
        border-radius: 50%;
    }
}
</style>
